import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element'
import { PUSH_NOTIFICATION_EVENTS } from '@doss/common-all'
import { isNil } from 'lodash'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { PanelImperativeHandle } from 'react-resizable-panels'
import invariant from 'tiny-invariant'
import { Panel, PanelGroup, PanelSeparator } from '@/components/ui/resizable'
import { cn } from '@/lib/utils'
import { usePushNotificationSubscription } from '~/common/generated/apollo-operations'
import { useBreadcrumbs } from '~/common/hooks/useBreadcrumbs'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { composeEmbedTitle, useEmbedPageTitle, useEmbedTitle } from '~/common/hooks/useEmbedTitle'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { usePage } from '~/common/hooks/usePage'
import { useSharedObject } from '~/common/hooks/useSharedObject'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { breadcrumbTitle } from '~/common/utils/breadcrumbs'
import { storeRecentPage } from '~/common/utils/local-storage'
import { collapse, expand, onCollapseExpand } from '~/common/utils/resizable'
import { TimelineDrawer } from '~/components/activity/TimelineDrawer'
import { ActivityPushNotification } from './activity/ActivityPushNotification'
import { EnvironmentBanner } from './EnvironmentBanner'
import { FloatingChatWidget } from './FloatingChatWidget'
import { ImpersonationBanner } from './ImpersonationBanner'
import { LeftNav } from './left-nav/LeftNav'
import { Navigation } from './navigation/Navigation'
import { TitleBar } from './navigation/TitleBar'
import { DossSnackbar, SnackbarType } from './shared/DossSnackbar'
import { VersioningBanner } from './VersioningBanner'

interface LayoutProps {
  children: ReactNode
}

/** Sets document.title reactively without next/head */
const DocumentTitle = ({ title }: { title: string }) => {
  useEffect(() => {
    document.title = title
  }, [title])
  return null
}

export const mainContentId = 'main_content'

/*
 * This is the main layout component that wraps all the pages in the application.
 * It includes universal components that support the framing of the app
 * and keeps the full screen covered in a consistent way.
 *
 * Scrollable areas of the layout are also contained within the layout component
 * and behave in a consistent way without scrolling the entire page or other areas.
 *
 * This framing makes sure that left nav, title bar,
 * main canvas, and footer each scroll independently of each other.
 */
export function Layout(props: LayoutProps) {
  const { t } = useTypedTranslation()
  const { user, isAdmin, isInternal } = useGlobalContext()
  const {
    isPageWithActivityFeed,
    isPageWithLeftNavCollapsed,
    isSharedPath,
    isPageWithBackLink,
    pageType,
    pathname,
    objectId,
    objectType,
    group,
  } = usePage()
  const { breadcrumbs, loading: breadcrumbsLoading } = useBreadcrumbs()
  const embedTitle = useEmbedTitle()
  const embedPageTitle = useEmbedPageTitle()
  const isFormPage = group === 'forms'
  const isDossbotPage = group === 'dossbot'
  const isDependencyExplorerPage = pageType === 'dependencyExplorer'
  const isWorkspacePage = pageType === 'workspace' || pageType === 'createWorkspace'
  const [isLeftNavExpanded, setIsLeftNavExpanded] = useState(!isPageWithLeftNavCollapsed)
  const [isLeftNavPeeking, setIsLeftNavPeeking] = useState(false)
  const [isActivityFeedExpanded, setIsActivityFeedExpanded] = useState(false)
  const { dispatch, subscribe, unsubscribe } = useDossSignals()
  const [newNotification, setNewNotification] = useState<JSX.Element | null>(null)
  const { data } = usePushNotificationSubscription({ skip: isNil(user) })
  const leftPanelRef = useRef<PanelImperativeHandle>(null)
  const rightPanelRef = useRef<PanelImperativeHandle>(null)
  const titleBarRef = useRef<HTMLDivElement>(null)
  const [titleBarBottom, setTitleBarBottom] = useState(0)
  const sharedObjectData = useSharedObject()
  const lastStoredPathRef = useRef<string>('')

  // Track page visits in localStorage for the Recently Visited section on the home page
  useEffect(() => {
    if (!user?.id || !objectId || !objectType || !breadcrumbs.length) {
      return
    }
    if (breadcrumbsLoading) {
      return
    }
    if (['home', 'settings', 'dossbot'].includes(pageType ?? '')) {
      return
    }
    if (pathname === lastStoredPathRef.current) {
      return
    }
    const finalCrumb = breadcrumbs.at(-1)
    if (!finalCrumb?.name) {
      return
    }
    lastStoredPathRef.current = pathname
    const parentCrumb = breadcrumbs.length > 1 ? breadcrumbs.at(-2) : undefined
    storeRecentPage(user.id, {
      id: objectId,
      objectType,
      name: finalCrumb.name,
      path: pathname,
      parentName: parentCrumb?.name,
    })
  }, [user?.id, pathname, objectId, objectType, breadcrumbs, breadcrumbsLoading, pageType])

  useEffect(() => {
    if (isNil(data)) {
      return
    }

    // Create a visible push notification of the event
    if (PUSH_NOTIFICATION_EVENTS.includes(data.pushNotification.event.type)) {
      setNewNotification(<ActivityPushNotification event={data.pushNotification} />)
    }
  }, [data])

  useEffect(() => {
    // this function is responsible for keeping the state of the left nav in sync
    // with its resizable panel ref and sends the rest of the app the signal of its state
    if (isLeftNavExpanded) {
      dispatch(DossSignalType.LEFT_NAV_EXPANDED)
      leftPanelRef.current?.isCollapsed() && expand(leftPanelRef.current)
    } else {
      dispatch(DossSignalType.LEFT_NAV_COLLAPSED)
      leftPanelRef.current?.isCollapsed() === false && collapse(leftPanelRef.current)
    }
  }, [isLeftNavExpanded, dispatch])

  useEffect(() => {
    // this function is responsible for keeping the state of the activity feed in sync
    // with its resizable panel ref and sends the rest of the app the signal of its state
    if (isActivityFeedExpanded) {
      dispatch(DossSignalType.ACTIVITY_FEED_EXPANDED)
      rightPanelRef.current?.isCollapsed() && expand(rightPanelRef.current)
    } else {
      dispatch(DossSignalType.ACTIVITY_FEED_COLLAPSED)
      rightPanelRef.current?.isCollapsed() === false && collapse(rightPanelRef.current)
    }
  }, [isActivityFeedExpanded, dispatch])

  useEffect(() => {
    // handle the signals from the rest of the app that want to open the activity feed
    const activityExpand = subscribe(DossSignalType.EXPAND_ACTIVITY_FEED_CLICKED, async () => {
      leftPanelRef.current?.isCollapsed() === false && setIsLeftNavExpanded(false)
      setIsActivityFeedExpanded(true)
      rightPanelRef.current?.isCollapsed() && expand(rightPanelRef.current)
    })

    // handles the signals from the rest of the app that want to close the activity feed
    const activityCollapse = subscribe(DossSignalType.COLLAPSE_ACTIVITY_FEED_CLICKED, async () => {
      if (!isPageWithLeftNavCollapsed) {
        setIsLeftNavExpanded(true)
        leftPanelRef.current?.isCollapsed() && expand(leftPanelRef.current)
      }
      setIsActivityFeedExpanded(false)
      rightPanelRef.current?.isCollapsed() === false && collapse(rightPanelRef.current)
    })

    // handle the signals from the rest of the app that want to open the left nav
    const leftNavExpand = subscribe(DossSignalType.EXPAND_LEFT_NAV_CLICKED, async () => {
      setIsLeftNavExpanded(true)
      leftPanelRef.current?.isCollapsed() && expand(leftPanelRef.current)
    })

    // handles the signals from the rest of the app that want to close the left
    // In the left nav collapse handler
    const leftNavCollapse = subscribe(DossSignalType.COLLAPSE_LEFT_NAV_CLICKED, async () => {
      setIsLeftNavExpanded(false)
      leftPanelRef.current?.isCollapsed() === false && collapse(leftPanelRef.current)
    })

    return () => {
      // clean up the subscriptions and reset the state of the layout
      unsubscribe(activityExpand)
      unsubscribe(activityCollapse)
      unsubscribe(leftNavExpand)
      unsubscribe(leftNavCollapse)
      setIsLeftNavExpanded(!isPageWithLeftNavCollapsed)
      setIsActivityFeedExpanded(false)
    }
  }, [isPageWithLeftNavCollapsed, subscribe, unsubscribe])

  useEffect(() => {
    // neither panel should appear if the user is logged out
    if (isSharedPath || !user) {
      setIsLeftNavExpanded(false)
      setIsActivityFeedExpanded(false)
    }
  }, [isSharedPath, user])

  useEffect(() => {
    if (!isPageWithActivityFeed) {
      setIsActivityFeedExpanded(false)
    }
  }, [isPageWithActivityFeed])

  useEffect(() => {
    if (!isPageWithLeftNavCollapsed) {
      // default to expanded when landing on a page with left nav
      setIsLeftNavExpanded(true)
      leftPanelRef.current?.isCollapsed() && expand(leftPanelRef.current)
    } else {
      setIsLeftNavExpanded(false)
      leftPanelRef.current?.isCollapsed() === false && collapse(leftPanelRef.current)
    }
  }, [isPageWithLeftNavCollapsed])

  const dragAndDropAutoScrollContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!isWorkspacePage) {
      return
    }
    const dragAndDropAutoScrollContainer = dragAndDropAutoScrollContainerRef.current
    invariant(dragAndDropAutoScrollContainer)
    const cleanUpAutoScroll = autoScrollForElements({
      element: dragAndDropAutoScrollContainer,
      canScroll: ({ source }) => source.data.type === 'view-in-workspace',
    })
    return () => {
      cleanUpAutoScroll()
    }
  }, [isWorkspacePage])

  // Measure TitleBar position for floating nav positioning
  useEffect(() => {
    const updateTitleBarPosition = () => {
      if (titleBarRef.current) {
        const rect = titleBarRef.current.getBoundingClientRect()
        setTitleBarBottom(rect.bottom)
      }
    }
    updateTitleBarPosition()
  }, [])

  // Only show the backlink for admin or internal users because the backlink for form pages
  // is the forms list page which will route non-admin users to the 404 page
  const showBackLink = isPageWithBackLink && (isAdmin || isInternal)

  const { isMobile } = useDeviceType()

  return (
    <>
      <DocumentTitle
        title={embedTitle ?? composeEmbedTitle(breadcrumbTitle(t, breadcrumbs), embedPageTitle)}
      />
      <div
        className={cn(
          'flex h-dvh w-screen flex-col overflow-hidden md:pb-0',
          !isFormPage && pageType !== 'record' && 'pb-[84px]',
        )}
      >
        {/* Environment, Versioning, and Impersonation Banners */}
        <EnvironmentBanner />
        <VersioningBanner />
        <ImpersonationBanner />

        {/* Mobile Navigation - only renders on mobile */}
        <Navigation isUnauthenticatedSharedRoute={isSharedPath && !user} />

        <div className="isolate flex h-full max-h-full flex-1 flex-row overflow-hidden">
          <PanelGroup className="size-full" orientation="horizontal">
            {!isMobile && (
              <Panel
                collapsedSize={0}
                collapsible
                defaultSize={240}
                maxSize={300}
                minSize={200}
                onResize={onCollapseExpand(
                  () => dispatch(DossSignalType.COLLAPSE_LEFT_NAV_CLICKED),
                  () => dispatch(DossSignalType.EXPAND_LEFT_NAV_CLICKED),
                )}
                panelRef={leftPanelRef}
              >
                <LeftNav expanded={isLeftNavExpanded} />
              </Panel>
            )}
            <PanelSeparator />
            <Panel className="overflow-hidden">
              <div className="flex h-full flex-col overflow-hidden">
                <div ref={titleBarRef}>
                  <TitleBar
                    isLeftNavExpanded={isLeftNavExpanded}
                    isLeftNavPeeking={isLeftNavPeeking}
                    isUnauthenticatedSharedRoute={isSharedPath && !user}
                    onLeftNavPeekEnd={() => setIsLeftNavPeeking(false)}
                    onLeftNavPeekStart={() => setIsLeftNavPeeking(true)}
                    sharedObject={sharedObjectData}
                    showBackLink={showBackLink}
                  />
                </div>
                <div
                  ref={dragAndDropAutoScrollContainerRef}
                  className="main-content flex size-full max-h-full flex-1 flex-col overflow-auto"
                  id={mainContentId}
                >
                  {props.children}
                </div>
              </div>
            </Panel>
            {isPageWithActivityFeed && (
              <>
                <PanelSeparator />
                <Panel
                  className="size-full"
                  collapsedSize={0}
                  collapsible
                  defaultSize={350}
                  maxSize={500}
                  minSize={300}
                  onResize={onCollapseExpand(
                    () => dispatch(DossSignalType.COLLAPSE_ACTIVITY_FEED_CLICKED),
                    () => dispatch(DossSignalType.EXPAND_ACTIVITY_FEED_CLICKED),
                  )}
                  panelRef={rightPanelRef}
                >
                  <div className="flex size-full flex-1 flex-col overflow-auto border-s">
                    {isActivityFeedExpanded && (
                      <TimelineDrawer
                        onClose={() => {
                          dispatch(DossSignalType.COLLAPSE_ACTIVITY_FEED_CLICKED)
                        }}
                      />
                    )}
                  </div>
                </Panel>
              </>
            )}
          </PanelGroup>
        </div>
      </div>
      <DossSnackbar
        duration={5000}
        horizontal="right"
        jsxMessage={newNotification ?? <></>}
        onClose={() => setNewNotification(null)}
        open={newNotification !== null}
        severity={SnackbarType.PUSH}
        vertical="top"
      />
      {user &&
        !isSharedPath &&
        !isDossbotPage &&
        !isFormPage &&
        !isDependencyExplorerPage &&
        !isMobile && <FloatingChatWidget />}

      {/* Floating Left Nav Peek Overlay */}
      {!isLeftNavExpanded && !isMobile && (
        <>
          {/* Invisible hover bridge below TitleBar - captures hover events */}
          <div
            className={`fixed left-0 z-[1390] w-[320px] transition-transform duration-300 ease-in-out ${
              isLeftNavPeeking ? 'translate-x-0' : '-translate-x-full'
            }`}
            onMouseEnter={() => {
              setIsLeftNavPeeking(true)
            }}
            onMouseLeave={() => {
              setIsLeftNavPeeking(false)
            }}
            style={{
              pointerEvents: isLeftNavPeeking ? 'auto' : 'none',
              background: 'transparent',
              top: `${titleBarBottom - 16}px`,
              left: '0px',
              height: '50px',
            }}
          />
          {/* Floating Left Nav */}
          <div
            className={`fixed bottom-11 left-0 z-[1400] w-[240px] overflow-hidden rounded-md shadow-lg transition-transform duration-300 ease-in-out ${
              isLeftNavPeeking ? 'translate-x-0' : '-translate-x-full'
            }`}
            onMouseEnter={() => {
              setIsLeftNavPeeking(true)
            }}
            onMouseLeave={() => {
              setIsLeftNavPeeking(false)
            }}
            style={{ top: `${titleBarBottom + 4}px` }}
          >
            <LeftNav expanded={true} isPeeking={isLeftNavPeeking} />
          </div>
        </>
      )}
    </>
  )
}
